// Phase D — Personal Playbook Adherence widget (TFVF V1.2). Renders on
// the agent's My Performance page. Fetches `/tfvf/v1_2/my-playbook-adherence`
// and surfaces three headers (Quality / Volume / Outcome) + a per-archetype
// scorecard, with a "How this is graded" popover that explains the shadow
// grader + snoozes so agents trust the number.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Loader2, Target, TrendingUp, Award, HelpCircle, X, BookOpen, ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hit-rate → colour band (mirrors the admin scorecard palette so agents
// see the same colour their manager sees).
const _pctTone = (hr) => {
  if (hr == null) return 'text-slate-400 bg-slate-100';
  if (hr >= 0.8) return 'text-emerald-700 bg-emerald-100';
  if (hr >= 0.6) return 'text-teal-700 bg-teal-100';
  if (hr >= 0.4) return 'text-amber-700 bg-amber-100';
  return 'text-rose-700 bg-rose-100';
};

const _pct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);

const HowGradedModal = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="how-graded-modal-overlay"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="flex items-center gap-2 text-slate-900">
            <BookOpen className="w-4 h-4 text-teal-600" />
            <h3 className="font-bold">How my Playbook Adherence is graded</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100"
            data-testid="how-graded-modal-close"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <div className="p-4 space-y-3 text-sm text-slate-700 leading-relaxed">
          <p>
            After every call, an AI Coach compares your transcript against the
            archetype playbook your admin drafted for calls of that type
            (Callback, New Enquiry, Tour Follow-up, etc). Each step in the
            playbook is scored:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b className="text-emerald-700">Hit</b> — you clearly landed the step.</li>
            <li><b className="text-amber-700">Partial</b> — you touched it, didn't fully close.</li>
            <li><b className="text-rose-700">Missed</b> — the transcript shows no attempt.</li>
          </ul>
          <p>
            Your <b>Quality</b> header is the mean hit-rate across every coached
            call in the window (partial = 0.5).
          </p>
          <p>
            Your <b>Volume</b> is how many of your calls were long enough to
            coach (transcripts under 40 words are skipped).
          </p>
          <p>
            Your <b>Outcome</b> is the single step you miss most often — the
            fastest lever to lift your hit-rate this week.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded p-3">
            <p className="text-xs text-amber-900">
              <b>Snoozed steps count in your favour.</b> If you tap the
              &ldquo;Deflected&rdquo; button mid-call for a step the caller
              derailed, the coach will note it and won&rsquo;t penalise you for
              that step.
            </p>
          </div>
          <p className="text-xs text-slate-500 italic">
            This score is coaching signal, not a KPI — your manager sees it
            beside your Quality / Volume / Outcome leaderboards, not instead of
            them.
          </p>
        </div>
      </div>
    </div>
  );
};

const MyPlaybookAdherenceWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [howOpen, setHowOpen] = useState(false);
  const [days] = useState(30);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/tfvf/v1_2/my-playbook-adherence?days=${days}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ scorecard: [], quality: null, volume: 0 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="my-playbook-adherence-card">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <Target className="w-4 h-4 text-teal-600" />
          My Playbook Adherence
          <span className="text-xs font-normal text-slate-500">last {days} days</span>
        </CardTitle>
        <button
          type="button"
          onClick={() => setHowOpen(true)}
          className="text-xs font-normal text-slate-400 hover:text-slate-600 inline-flex items-center gap-1"
          data-testid="my-adherence-how-graded"
        >
          How this is graded <HelpCircle className="w-3 h-3" />
        </button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="w-4 h-4 text-slate-400 animate-spin" /></div>
        ) : (data?.volume ?? 0) === 0 ? (
          <p className="text-sm text-slate-500 py-2">
            No coached calls in the last {days} days yet. Your playbook
            adherence shows up here after the AI Coach runs on your calls.
          </p>
        ) : (
          <>
            {/* Q/V/O headers */}
            <div className="grid grid-cols-3 gap-2 mb-4" data-testid="my-adherence-qvo">
              <div className="rounded border border-slate-200 p-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
                  <Award className="w-3 h-3 text-emerald-500" /> Quality
                </div>
                <div className={`text-xl font-bold tabular-nums mt-1 ${_pctTone(data.quality).split(' ')[0]}`}>
                  {_pct(data.quality)}
                </div>
                <div className="text-[10px] text-slate-500">avg hit-rate</div>
              </div>
              <div className="rounded border border-slate-200 p-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
                  <TrendingUp className="w-3 h-3 text-teal-500" /> Volume
                </div>
                <div className="text-xl font-bold tabular-nums mt-1 text-slate-900">{data.volume}</div>
                <div className="text-[10px] text-slate-500">coached calls</div>
              </div>
              <div className="rounded border border-slate-200 p-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
                  <Target className="w-3 h-3 text-amber-500" /> Outcome
                </div>
                <div className="text-sm font-semibold text-slate-900 mt-1 line-clamp-2" title={data.outcome?.step_summary || ''}>
                  {data.outcome ? data.outcome.step_summary : 'No repeat misses'}
                </div>
                <div className="text-[10px] text-slate-500">
                  {data.outcome ? `missed on ${_pct(data.outcome.miss_pct)} of calls` : 'top gap'}
                </div>
              </div>
            </div>

            {/* Per-archetype rows */}
            <div className="space-y-1.5" data-testid="my-adherence-scorecard">
              {data.scorecard.map((row) => (
                <div
                  key={row.archetype}
                  className="grid grid-cols-12 items-center gap-2 rounded border border-slate-100 hover:border-slate-200 px-2 py-1.5 text-sm"
                  data-testid={`my-adherence-row-${row.archetype}`}
                >
                  <div className="col-span-4 flex items-center gap-1.5">
                    <span className="text-teal-700 font-mono text-[11px] bg-teal-50 border border-teal-100 rounded px-1.5 py-0.5">
                      {row.archetype}
                    </span>
                  </div>
                  <div className="col-span-2 text-xs text-slate-500 tabular-nums">
                    {row.sample_size} call{row.sample_size === 1 ? '' : 's'}
                  </div>
                  <div className="col-span-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${_pctTone(row.avg_hit_rate)}`}>
                      {_pct(row.avg_hit_rate)}
                    </span>
                  </div>
                  <div className="col-span-3 text-right">
                    {row.worst_call_id && (
                      <Link
                        to={`/calls/${row.worst_call_id}`}
                        className="text-xs text-teal-600 hover:text-teal-700 inline-flex items-center gap-0.5"
                        data-testid={`my-adherence-open-worst-${row.archetype}`}
                      >
                        review worst <ChevronRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
      <HowGradedModal open={howOpen} onClose={() => setHowOpen(false)} />
    </Card>
  );
};

export default MyPlaybookAdherenceWidget;
